/**
 * Optional features that may be missing in a browser (W1-16 app-integration; ARCHITECTURE.md §15): their names and
 * the rejection of every call to one that could not start. A leaf module so that the services wiring
 * (services.ts), the project session (projectSession.ts) and the audio fallback (audioFallback.ts) share it without
 * importing each other.
 */

/** Parts of the editor that may be missing in a browser without breaking the rest. */
export type OptionalFeature = 'audio' | 'io' | 'dsp' | 'analysis' | 'skins' | 'problems' | 'textures';

export interface UnavailableFeature {
  readonly feature: OptionalFeature;
  /** Why (the browser's own text, or a translated reason when it gave none). */
  readonly reason: string;
}

/** Rejection of every call to a feature that could not start. */
export class FeatureUnavailableError extends Error {
  readonly code = 'unavailable';
  readonly feature: OptionalFeature;
  /** Why the feature is missing (see UnavailableFeature.reason). */
  readonly reason: string;
  /**
   * @param message the text a caller may show; the services build it translated ("Audio playback is not available
   *   in this browser: ..."). Default: English developer text.
   */
  constructor(feature: OptionalFeature, reason: string, message = `${feature} is not available: ${reason}`) {
    super(message);
    this.name = 'FeatureUnavailableError';
    this.feature = feature;
    this.reason = reason;
  }
}

export function isFeatureUnavailable(err: unknown): err is FeatureUnavailableError {
  return err instanceof FeatureUnavailableError;
}

/** Builds the rejection for a feature (the services pass one that translates the message). */
export type UnavailableErrorFactory = (feature: OptionalFeature, reason: string) => FeatureUnavailableError;

export const defaultUnavailableError: UnavailableErrorFactory = (feature, reason) =>
  new FeatureUnavailableError(feature, reason);
